import { useParams } from 'react-router-dom';

import { useStore } from '../state';
import type { ModelTypeMap, ResourceName, ResourceParamName } from './useIdParams.types';
import { resourceToCollectionMap } from './useIdParams.types';

/**
 * Hook to fetch a model from the URL params with proper type inference.
 * @param resource - The resource name (e.g., 'campaign', 'template')
 * @returns The model with the correct type based on the resource.
 *
 * @example
 * const campaign = useIdParams('campaign'); // Looks for 'campaignId' in URL params
 * const template = useIdParams('template'); // Looks for 'templateId' in URL params
 */
export default function useIdParams<T extends ResourceName>(
  resource: T,
): ModelTypeMap[ResourceParamName<T>] {
  const paramName = `${resource}Id` as ResourceParamName<T>;
  const params = useParams<Record<string, string>>();
  const modelId = params[paramName];
  const collectionName = resourceToCollectionMap[resource];
  const model = useStore()[collectionName].get(modelId);
  return model as ModelTypeMap[ResourceParamName<T>];
}
